import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Download, 
  Trash2,
  Calendar,
  Edit,
  Camera,
  Clock,
  Truck,
  Wrench,
  Plus,
  Eye
} from 'lucide-react';
import { formatearFecha, formatearFechaHora } from '@/utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import TopLevelLayout from '@/components/TopLevelLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPOS_DOCUMENTO = [
  'Registro del Vehículo',
  'Revisión Técnica',
  'Marchamo',
  'Seguro (INS)',
  'Contrato de Compra',
  'Factura de Compra',
  'Póliza de Seguro',
  'Comprobante de Mantenimiento',
  'Certificado de Emisiones',
  'Otro'
];

export default function ExpedienteVehiculo({ user, onLogout }) {
  const { vehiculoId } = useParams();
  const navigate = useNavigate();

  const [vehiculo, setVehiculo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Estados para subir documento
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);

  // Estados para eliminar documento
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [documentoAEliminar, setDocumentoAEliminar] = useState(null);

  // Estado para vista previa
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [documentoParaVer, setDocumentoParaVer] = useState(null);

  // Estados para mantenimientos
  const [mantenimientos, setMantenimientos] = useState([]);
  const [loadingMantenimientos, setLoadingMantenimientos] = useState(false);
  const [showMantenimientoDialog, setShowMantenimientoDialog] = useState(false);
  const [editingMantenimiento, setEditingMantenimiento] = useState(null);
  const [mantenimientoForm, setMantenimientoForm] = useState({
    tipo: 'preventivo',
    fecha_mantenimiento: new Date().toISOString().split('T')[0],
    kilometraje: '',
    horas_uso: '',
    descripcion: '',
    acciones_realizadas: '',
    repuestos: [],
    costo_mano_obra: '',
    mecanico_responsable: '',
    proximo_mantenimiento_fecha: '',
    proximo_mantenimiento_km: '',
    estado_vehiculo: 'operativo',
    observaciones: ''
  });
  const [showDeleteMantenimientoDialog, setShowDeleteMantenimientoDialog] = useState(false);
  const [mantenimientoAEliminar, setMantenimientoAEliminar] = useState(null);

  useEffect(() => {
    if (vehiculoId) {
      fetchVehiculo();
      fetchMantenimientos();
    } else {
      setError('ID de vehículo no proporcionado');
      setLoading(false);
    }
  }, [vehiculoId]);

  const fetchVehiculo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== FETCHING VEHICULO ===');
      console.log('VehiculoId from params:', vehiculoId);
      console.log('API base:', API);
      console.log('Full URL:', `${API}/vehiculos/${vehiculoId}`);
      
      const response = await axios.get(`${API}/vehiculos/${vehiculoId}`);
      
      console.log('✓ Response status:', response.status);
      console.log('✓ Response data:', response.data);
      
      if (response.data) {
        setVehiculo(response.data);
        toast.success('Expediente cargado correctamente');
      } else {
        throw new Error('No se recibieron datos del vehículo');
      }
    } catch (error) {
      console.error('=== ERROR FETCHING VEHICULO ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      let errorMessage = 'Error al cargar el vehículo';
      
      if (error.response) {
        // El servidor respondió con un código de error
        if (error.response.status === 404) {
          errorMessage = 'Vehículo no encontrado';
        } else if (error.response.status === 401) {
          errorMessage = 'No autorizado. Por favor inicia sesión nuevamente';
        } else {
          errorMessage = error.response.data?.detail || `Error del servidor (${error.response.status})`;
        }
      } else if (error.request) {
        // La petición se hizo pero no hubo respuesta
        errorMessage = 'No se pudo conectar con el servidor';
      } else {
        // Error en la configuración de la petición
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Solo navegar de vuelta si es un error 404
      if (error.response?.status === 404) {
        setTimeout(() => navigate('/maquinaria'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMantenimientos = async () => {
    try {
      setLoadingMantenimientos(true);
      const response = await axios.get(`${API}/mantenimientos/vehiculo/${vehiculoId}`);
      setMantenimientos(response.data);
    } catch (error) {
      console.error('Error al cargar mantenimientos:', error);
      toast.error('Error al cargar los mantenimientos');
    } finally {
      setLoadingMantenimientos(false);
    }
  };

  const handleOpenMantenimientoDialog = (mantenimiento = null) => {
    if (mantenimiento) {
      setEditingMantenimiento(mantenimiento);
      setMantenimientoForm({
        tipo: mantenimiento.tipo,
        fecha_mantenimiento: mantenimiento.fecha_mantenimiento,
        kilometraje: mantenimiento.kilometraje || '',
        horas_uso: mantenimiento.horas_uso || '',
        descripcion: mantenimiento.descripcion,
        acciones_realizadas: mantenimiento.acciones_realizadas || '',
        repuestos: mantenimiento.repuestos || [],
        costo_mano_obra: mantenimiento.costo_mano_obra || '',
        mecanico_responsable: mantenimiento.mecanico_responsable || '',
        proximo_mantenimiento_fecha: mantenimiento.proximo_mantenimiento_fecha || '',
        proximo_mantenimiento_km: mantenimiento.proximo_mantenimiento_km || '',
        estado_vehiculo: mantenimiento.estado_vehiculo,
        observaciones: mantenimiento.observaciones || ''
      });
    } else {
      setEditingMantenimiento(null);
      setMantenimientoForm({
        tipo: 'preventivo',
        fecha_mantenimiento: new Date().toISOString().split('T')[0],
        kilometraje: '',
        horas_uso: '',
        descripcion: '',
        acciones_realizadas: '',
        repuestos: [],
        costo_mano_obra: '',
        mecanico_responsable: '',
        proximo_mantenimiento_fecha: '',
        proximo_mantenimiento_km: '',
        estado_vehiculo: vehiculo?.estado_actual || 'operativo',
        observaciones: ''
      });
    }
    setShowMantenimientoDialog(true);
  };

  const handleSaveMantenimiento = async () => {
    // Validaciones
    if (!mantenimientoForm.tipo || !mantenimientoForm.fecha_mantenimiento || !mantenimientoForm.descripcion) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    try {
      const payload = {
        ...mantenimientoForm,
        vehiculo_id: vehiculoId,
        usuario_registro: user.username,
        kilometraje: mantenimientoForm.kilometraje ? parseInt(mantenimientoForm.kilometraje) : null,
        horas_uso: mantenimientoForm.horas_uso ? parseInt(mantenimientoForm.horas_uso) : null,
        costo_mano_obra: mantenimientoForm.costo_mano_obra ? parseFloat(mantenimientoForm.costo_mano_obra) : 0,
        proximo_mantenimiento_km: mantenimientoForm.proximo_mantenimiento_km ? parseInt(mantenimientoForm.proximo_mantenimiento_km) : null
      };

      if (editingMantenimiento) {
        // Actualizar
        await axios.put(`${API}/mantenimientos/${editingMantenimiento.id}`, payload);
        toast.success('Mantenimiento actualizado exitosamente');
      } else {
        // Crear
        await axios.post(`${API}/mantenimientos`, payload);
        toast.success('Mantenimiento registrado exitosamente');
      }

      setShowMantenimientoDialog(false);
      fetchMantenimientos();
      fetchVehiculo(); // Actualizar el vehículo por si cambió el estado
    } catch (error) {
      console.error('Error al guardar mantenimiento:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el mantenimiento');
    }
  };

  const handleDeleteMantenimiento = async () => {
    if (!mantenimientoAEliminar) return;

    try {
      await axios.delete(`${API}/mantenimientos/${mantenimientoAEliminar.id}`);
      toast.success('Mantenimiento eliminado');
      setShowDeleteMantenimientoDialog(false);
      setMantenimientoAEliminar(null);
      fetchMantenimientos();
    } catch (error) {
      console.error('Error al eliminar mantenimiento:', error);
      toast.error('Error al eliminar el mantenimiento');
    }
  };

  const handleUploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploaded_by', user.username);

    try {
      await axios.post(`${API}/vehiculos/${vehiculoId}/foto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Foto actualizada exitosamente');
      fetchVehiculo();
    } catch (error) {
      toast.error('Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleOpenUploadDialog = () => {
    setTipoDocumento('');
    setArchivoSeleccionado(null);
    setShowUploadDialog(true);
  };

  const handleUploadDocument = async () => {
    if (!tipoDocumento || !archivoSeleccionado) {
      toast.error('Selecciona un tipo de documento y un archivo');
      return;
    }

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('tipo', tipoDocumento);
    formData.append('file', archivoSeleccionado);
    formData.append('uploaded_by', user.username);

    try {
      await axios.post(`${API}/vehiculos/${vehiculoId}/documentos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento subido exitosamente');
      setShowUploadDialog(false);
      fetchVehiculo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentoAEliminar) return;

    try {
      await axios.delete(
        `${API}/vehiculos/${vehiculoId}/documentos/${documentoAEliminar.id}`,
        { params: { deleted_by: user.username } }
      );
      toast.success('Documento eliminado exitosamente');
      setShowDeleteDialog(false);
      setDocumentoAEliminar(null);
      fetchVehiculo();
    } catch (error) {
      toast.error('Error al eliminar el documento');
    }
  };

  if (loading) {
    return (
      <TopLevelLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <p>Cargando expediente...</p>
        </div>
      </TopLevelLayout>
    );
  }

  if (!vehiculo) {
    return (
      <TopLevelLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <p>Vehículo no encontrado</p>
        </div>
      </TopLevelLayout>
    );
  }

  return (
    <TopLevelLayout user={user} onLogout={onLogout} activeTab="agricultura">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/maquinaria')}
            className="flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Maquinaria
          </Button>
        </div>

        {/* Información del Vehículo y Foto */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Truck className="w-12 h-12 text-blue-600" />
                <div>
                  <CardTitle className="text-2xl text-gray-900">
                    {vehiculo.marca} {vehiculo.modelo}
                  </CardTitle>
                  <p className="text-gray-600 mt-1">Placa: {vehiculo.placa}</p>
                  <Badge className={vehiculo.estado_actual === 'Activo' ? 'bg-green-600' : 'bg-gray-600'}>
                    {vehiculo.estado_actual}
                  </Badge>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Foto del Vehículo */}
              <div className="space-y-2">
                <Label>Foto del Vehículo</Label>
                <div className="relative">
                  {vehiculo.foto_vehiculo ? (
                    <img
                      src={`${BACKEND_URL}${vehiculo.foto_vehiculo}`}
                      alt="Foto del vehículo"
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    id="photo-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleUploadPhoto}
                  />
                  <Button
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => document.getElementById('photo-upload').click()}
                    disabled={uploadingPhoto}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {uploadingPhoto ? 'Subiendo...' : 'Cambiar Foto'}
                  </Button>
                </div>
              </div>

              {/* Información General */}
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Tipo</Label>
                  <p className="font-semibold">{vehiculo.tipo_vehiculo}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Año</Label>
                  <p className="font-semibold">{vehiculo.ano}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Color</Label>
                  <p className="font-semibold">{vehiculo.color}</p>
                </div>
                <div>
                  <Label className="text-gray-600">VIN</Label>
                  <p className="font-semibold">{vehiculo.numero_vin || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Motor</Label>
                  <p className="font-semibold">{vehiculo.numero_motor || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Capacidad</Label>
                  <p className="font-semibold">{vehiculo.capacidad || 'N/A'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documentos */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Documentos del Vehículo
              </CardTitle>
              <Button onClick={handleOpenUploadDialog}>
                <Upload className="w-4 h-4 mr-2" />
                Subir Documento
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {vehiculo.documentos && vehiculo.documentos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehiculo.documentos.map((doc) => (
                  <Card key={doc.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => {
                              setDocumentoParaVer(doc);
                              setShowPreviewDialog(true);
                            }}
                            title="Ver documento"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => window.open(`${BACKEND_URL}/api/${doc.file_path}`, '_blank')}
                            title="Descargar documento"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setDocumentoAEliminar(doc);
                              setShowDeleteDialog(true);
                            }}
                            title="Eliminar documento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <h3 className="font-semibold text-sm mb-1">{doc.tipo}</h3>
                      <p className="text-xs text-gray-600 truncate mb-2">{doc.filename}</p>
                      <p className="text-xs text-gray-400">
                        {formatearFecha(doc.uploaded_at)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No hay documentos subidos</p>
                <p className="text-sm mt-2">Haz clic en &quot;Subir Documento&quot; para comenzar</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Módulo de Mantenimiento */}
        <Card>
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-blue-900">
                <Wrench className="w-5 h-5 mr-2" />
                Mantenimientos
              </CardTitle>
              <Button 
                onClick={() => handleOpenMantenimientoDialog()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Registrar Mantenimiento
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {loadingMantenimientos ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Cargando mantenimientos...</p>
              </div>
            ) : mantenimientos.length > 0 ? (
              <div className="space-y-4">
                {mantenimientos.map((mant) => (
                  <Card key={mant.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge 
                              className={
                                mant.tipo === 'preventivo' ? 'bg-green-600' :
                                mant.tipo === 'correctivo' ? 'bg-orange-600' :
                                'bg-blue-600'
                              }
                            >
                              {mant.tipo.charAt(0).toUpperCase() + mant.tipo.slice(1)}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {formatearFecha(mant.fecha_mantenimiento)}
                            </span>
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-1">{mant.descripcion}</h3>
                          {mant.acciones_realizadas && (
                            <p className="text-sm text-gray-600 mb-2">{mant.acciones_realizadas}</p>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            {mant.kilometraje && (
                              <div>
                                <span className="text-gray-500">Kilometraje:</span>
                                <p className="font-medium">{mant.kilometraje.toLocaleString()} km</p>
                              </div>
                            )}
                            {mant.horas_uso && (
                              <div>
                                <span className="text-gray-500">Horas de uso:</span>
                                <p className="font-medium">{mant.horas_uso} hrs</p>
                              </div>
                            )}
                            {mant.costo_total > 0 && (
                              <div>
                                <span className="text-gray-500">Costo Total:</span>
                                <p className="font-medium">₡{mant.costo_total.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</p>
                              </div>
                            )}
                            {mant.mecanico_responsable && (
                              <div>
                                <span className="text-gray-500">Mecánico:</span>
                                <p className="font-medium">{mant.mecanico_responsable}</p>
                              </div>
                            )}
                          </div>
                          {mant.proximo_mantenimiento_fecha && (
                            <div className="mt-2 p-2 bg-amber-50 rounded-md text-sm">
                              <span className="text-amber-700 font-medium">
                                Próximo mantenimiento: {formatearFecha(mant.proximo_mantenimiento_fecha)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 ml-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600"
                            onClick={() => handleOpenMantenimientoDialog(mant)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => {
                              setMantenimientoAEliminar(mant);
                              setShowDeleteMantenimientoDialog(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Wrench className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No hay mantenimientos registrados</p>
                <p className="text-sm mt-2">Haz clic en &quot;Registrar Mantenimiento&quot; para comenzar</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Historial de Cambios */}
        {vehiculo.historial_cambios && vehiculo.historial_cambios.length > 0 && (
          <Card className="border-0 shadow-md">
            <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
              <CardTitle className="text-lg text-emerald-900 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Historial del Expediente
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
                
                <div className="space-y-6">
                  {vehiculo.historial_cambios
                    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                    .map((cambio, index) => {
                      let icon = <FileText className="w-4 h-4 text-white" />;
                      let bgColor = 'bg-emerald-600';
                      
                      if (cambio.tipo === 'creacion') {
                        icon = <span className="text-white font-bold text-lg">+</span>;
                        bgColor = 'bg-blue-600';
                      } else if (cambio.tipo === 'documento_subido') {
                        icon = <Upload className="w-4 h-4 text-white" />;
                        bgColor = 'bg-green-600';
                      } else if (cambio.tipo === 'documento_eliminado') {
                        icon = <Trash2 className="w-4 h-4 text-white" />;
                        bgColor = 'bg-red-600';
                      } else if (cambio.tipo === 'foto_actualizada') {
                        icon = <Camera className="w-4 h-4 text-white" />;
                        bgColor = 'bg-purple-600';
                      }
                      
                      return (
                        <div key={cambio.id || index} className="relative pl-12">
                          <div className={`absolute left-0 top-1 w-8 h-8 ${bgColor} rounded-full flex items-center justify-center shadow-md`}>
                            {icon}
                          </div>
                          
                          <div className="bg-white p-4 rounded-lg border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                            <p className="font-semibold text-gray-900">{cambio.descripcion}</p>
                            {cambio.usuario && (
                              <p className="text-xs text-gray-500 mt-1">Por: {cambio.usuario}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-2 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatearFechaHora(cambio.fecha)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog para subir documento */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Tipo de Documento *</Label>
              <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {tipo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Archivo *</Label>
              <Input
                type="file"
                onChange={(e) => setArchivoSeleccionado(e.target.files[0])}
              />
              <p className="text-xs text-gray-500">Tamaño máximo: 10MB</p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowUploadDialog(false)}
                disabled={uploadingDoc}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUploadDocument}
                disabled={uploadingDoc || !tipoDocumento || !archivoSeleccionado}
              >
                {uploadingDoc ? 'Subiendo...' : 'Subir Documento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para eliminar documento */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El documento será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para crear/editar mantenimiento */}
      <Dialog open={showMantenimientoDialog} onOpenChange={setShowMantenimientoDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMantenimiento ? 'Editar Mantenimiento' : 'Registrar Nuevo Mantenimiento'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Mantenimiento *</Label>
                <Select 
                  value={mantenimientoForm.tipo} 
                  onValueChange={(value) => setMantenimientoForm({...mantenimientoForm, tipo: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventivo">Preventivo</SelectItem>
                    <SelectItem value="correctivo">Correctivo</SelectItem>
                    <SelectItem value="inspeccion">Inspección</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Fecha de Mantenimiento *</Label>
                <Input
                  type="date"
                  value={mantenimientoForm.fecha_mantenimiento}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, fecha_mantenimiento: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Kilometraje</Label>
                <Input
                  type="number"
                  placeholder="Ej: 45000"
                  value={mantenimientoForm.kilometraje}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, kilometraje: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Horas de Uso</Label>
                <Input
                  type="number"
                  placeholder="Ej: 1200"
                  value={mantenimientoForm.horas_uso}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, horas_uso: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción del Mantenimiento *</Label>
              <Input
                placeholder="Ej: Cambio de aceite y filtros"
                value={mantenimientoForm.descripcion}
                onChange={(e) => setMantenimientoForm({...mantenimientoForm, descripcion: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Acciones Realizadas</Label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Detalle de las acciones realizadas durante el mantenimiento..."
                value={mantenimientoForm.acciones_realizadas}
                onChange={(e) => setMantenimientoForm({...mantenimientoForm, acciones_realizadas: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Costo de Mano de Obra (₡)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={mantenimientoForm.costo_mano_obra}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, costo_mano_obra: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Mecánico Responsable</Label>
                <Input
                  placeholder="Nombre del mecánico"
                  value={mantenimientoForm.mecanico_responsable}
                  onChange={(e) => setMantenimientoForm({...mantenimientoForm, mecanico_responsable: e.target.value})}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-sm mb-3 text-gray-700">Próximo Mantenimiento</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Programada</Label>
                  <Input
                    type="date"
                    value={mantenimientoForm.proximo_mantenimiento_fecha}
                    onChange={(e) => setMantenimientoForm({...mantenimientoForm, proximo_mantenimiento_fecha: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Kilometraje Programado</Label>
                  <Input
                    type="number"
                    placeholder="Ej: 50000"
                    value={mantenimientoForm.proximo_mantenimiento_km}
                    onChange={(e) => setMantenimientoForm({...mantenimientoForm, proximo_mantenimiento_km: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Estado del Vehículo</Label>
              <Select 
                value={mantenimientoForm.estado_vehiculo} 
                onValueChange={(value) => setMantenimientoForm({...mantenimientoForm, estado_vehiculo: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operativo">Operativo</SelectItem>
                  <SelectItem value="fuera_servicio">Fuera de Servicio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Observaciones</Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Observaciones adicionales..."
                value={mantenimientoForm.observaciones}
                onChange={(e) => setMantenimientoForm({...mantenimientoForm, observaciones: e.target.value})}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowMantenimientoDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveMantenimiento}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingMantenimiento ? 'Actualizar' : 'Registrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para eliminar mantenimiento */}
      <AlertDialog open={showDeleteMantenimientoDialog} onOpenChange={setShowDeleteMantenimientoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar mantenimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El registro de mantenimiento será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMantenimiento} className="bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para visualizar documento */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {documentoParaVer?.tipo} - {documentoParaVer?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto" style={{ height: 'calc(90vh - 120px)' }}>
            {documentoParaVer && (
              <div className="w-full h-full">
                {documentoParaVer.filename?.toLowerCase().endsWith('.pdf') ? (
                  // Vista de PDF
                  <iframe
                    src={`${BACKEND_URL}/api/${documentoParaVer.file_path}`}
                    className="w-full h-full border-0"
                    title={documentoParaVer.filename}
                    style={{ minHeight: '600px' }}
                  />
                ) : documentoParaVer.filename?.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                  // Vista de imagen
                  <div className="flex items-center justify-center bg-gray-100 p-4 rounded-lg">
                    <img
                      src={`${BACKEND_URL}/api/${documentoParaVer.file_path}`}
                      alt={documentoParaVer.filename}
                      className="max-w-full max-h-full object-contain"
                      style={{ maxHeight: '70vh' }}
                    />
                  </div>
                ) : (
                  // Para otros tipos de archivos, mostrar información y botón de descarga
                  <div className="text-center py-12">
                    <FileText className="w-24 h-24 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-semibold mb-2">Vista previa no disponible</h3>
                    <p className="text-gray-600 mb-4">
                      Este tipo de archivo no se puede visualizar en el navegador.
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                      Tipo: {documentoParaVer.filename?.split('.').pop()?.toUpperCase()}
                    </p>
                    <Button
                      onClick={() => window.open(`${BACKEND_URL}/api/${documentoParaVer.file_path}`, '_blank')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Archivo
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-500">
              Subido el {documentoParaVer && formatearFechaHora(documentoParaVer.uploaded_at)}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(`${BACKEND_URL}/api/${documentoParaVer?.file_path}`, '_blank')}
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar
              </Button>
              <Button
                onClick={() => setShowPreviewDialog(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TopLevelLayout>
  );
}
